import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OfficeSwitcherProvider } from './contexts/OfficeSwitcherContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import ImpersonationBanner from './components/ImpersonationBanner';
import { ForcePasswordChangeModal } from './components/ForcePasswordChangeModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import { registerRouteFactories, recordVisit } from './utils/routePrefetch';
import { getDefaultLandingPage } from './utils/landingPage';
import './App.css';

// Eager — critical path (login screen + landing dashboard).
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const DriverTrackingPage = lazy(() => import(/* webpackChunkName: "driver-tracking" */ './pages/tracking/DriverTrackingPage'));
const PublicTrackingPage = lazy(() => import(/* webpackChunkName: "public-tracking" */ './pages/tracking/PublicTrackingPage'));
const PublicLanePage = lazy(() => import(/* webpackChunkName: "public-lane" */ './pages/PublicLanePage'));
const EldOAuthSuccess = lazy(() => import(/* webpackChunkName: "eld-oauth" */ './pages/eld/EldOAuthSuccess'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy" */ './pages/PrivacyPolicy'));

const CarrierPortalLogin = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalLogin'));
const CarrierPortalSignup = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalSignup'));
const CarrierPortalForgotPassword = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalForgotPassword'));
const CarrierPortalSetPassword = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalSetPassword'));
const CarrierPortalLayout = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalLayout'));
const CarrierPortalDashboard = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalDashboard'));
const CarrierPortalLoads = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalLoads'));
const CarrierPortalLoadDetail = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalLoadDetail'));
const CarrierPortalPayments = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalPayments'));
const CarrierPortalEld = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalEld'));
const CarrierPortalUsers = lazy(() => import(/* webpackChunkName: "cp" */ './pages/carrier-portal/CarrierPortalUsers'));

// Lazy — everything else. Each page becomes its own JS chunk so the initial
// bundle stays small and we only download what the user actually visits.
// Webpack chunk names keep DevTools network panel readable.
const Loads = lazy(() => import(/* webpackChunkName: "loads" */ './pages/Loads'));
const LoadDetail = lazy(() => import(/* webpackChunkName: "load-detail" */ './pages/LoadDetail'));
const BolEdit = lazy(() => import(/* webpackChunkName: "bol-edit" */ './pages/BolEdit'));
const LoadEdit = lazy(() => import(/* webpackChunkName: "load-edit" */ './pages/LoadEdit'));
const Customers = lazy(() => import(/* webpackChunkName: "customers" */ './pages/Customers'));
const MyDay = lazy(() => import(/* webpackChunkName: "my-day" */ './pages/MyDay'));
const LeadRecordPage = lazy(() => import(/* webpackChunkName: "crm-record" */ './pages/crm/LeadRecordPage'));
const CustomerRecordPage = lazy(() => import(/* webpackChunkName: "crm-record" */ './pages/crm/CustomerRecordPage'));
const Carriers = lazy(() => import(/* webpackChunkName: "carriers" */ './pages/Carriers'));
const CarrierDuplicates = lazy(() => import(/* webpackChunkName: "carriers" */ './pages/CarrierDuplicates'));
const CustomerDuplicates = lazy(() => import(/* webpackChunkName: "customer-duplicates" */ './pages/admin/CustomerDuplicates'));
const Invoices = lazy(() => import(/* webpackChunkName: "invoices" */ './pages/Invoices'));
const DataHealthConsole = lazy(() => import(/* webpackChunkName: "data-health-console" */ './pages/admin/DataHealthConsole'));
const OfficeIsolation = lazy(() => import(/* webpackChunkName: "office-isolation" */ './pages/admin/OfficeIsolation'));
const Settings = lazy(() => import(/* webpackChunkName: "settings" */ './pages/Settings'));
const MailboxHealth = lazy(() => import(/* webpackChunkName: "mailbox-health" */ './pages/admin/MailboxHealth'));
const EmailPipelineHealthPage = lazy(() => import(/* webpackChunkName: "email-pipeline-health" */ './pages/admin/EmailPipelineHealthPage'));
const OwnershipTransfers = lazy(() => import(/* webpackChunkName: "ownership-transfers" */ './pages/admin/OwnershipTransfers'));
const SecurityLogPage = lazy(() => import(/* webpackChunkName: "security-log" */ './pages/admin/SecurityLogPage'));
const MarginGoalSetting = lazy(() => import(/* webpackChunkName: "margin-goals" */ './pages/MarginGoalSetting'));
const Notifications = lazy(() => import(/* webpackChunkName: "notifications" */ './pages/Notifications'));
const Users = lazy(() => import(/* webpackChunkName: "users" */ './pages/Users'));
const Reports = lazy(() => import(/* webpackChunkName: "reports" */ './pages/Reports'));
const ScheduledReports = lazy(() => import(/* webpackChunkName: "scheduled-reports" */ './pages/ScheduledReports'));
const Integrations = lazy(() => import(/* webpackChunkName: "integrations" */ './pages/Integrations'));
const OrphanedDocuments = lazy(() => import(/* webpackChunkName: "orphaned-docs" */ './pages/OrphanedDocuments'));
const MissingDatesAdmin = lazy(() => import(/* webpackChunkName: "missing-dates" */ './pages/MissingDatesAdmin'));
const OrphanInvoicesPage = lazy(() => import(/* webpackChunkName: "orphan-invoices" */ './pages/admin/OrphanInvoicesPage'));
const MissingCarrierAdmin = lazy(() => import(/* webpackChunkName: "missing-carrier" */ './pages/MissingCarrierAdmin'));
const LoadsAudit = lazy(() => import(/* webpackChunkName: "loads-audit" */ './pages/LoadsAudit'));
const OpenDisputes = lazy(() => import(/* webpackChunkName: "open-disputes" */ './pages/admin/OpenDisputes'));
const GracePeriodsAudit = lazy(() => import(/* webpackChunkName: "grace-periods-audit" */ './pages/admin/GracePeriodsAudit'));
const InvoiceRemindersSettingsPage = lazy(() => import(/* webpackChunkName: "invoice-reminders" */ './pages/admin/InvoiceRemindersSettingsPage'));
const NotificationsCenterPage = lazy(() => import(/* webpackChunkName: "notifications-center" */ './pages/NotificationsCenterPage'));
const DuplicateInvoicesPage = lazy(() => import(/* webpackChunkName: "duplicate-invoices" */ './pages/DuplicateInvoicesPage'));
const ImapSentMirrorPage = lazy(() => import(/* webpackChunkName: "imap-sent-mirror" */ './pages/ImapSentMirrorPage'));
const SalesRepHealth = lazy(() => import(/* webpackChunkName: "sales-rep-health" */ './pages/admin/SalesRepHealth'));
const MailgunSuppressions = lazy(() => import(/* webpackChunkName: "mailgun-suppressions" */ './pages/admin/MailgunSuppressions'));
import { EntityCategoriesProvider } from './components/entity-categories/CategoryPicker';

const EmailManagementHub = lazy(() => import(/* webpackChunkName: "email-hub" */ './pages/admin/EmailManagementHub'));
const CreditRiskFlags = lazy(() => import(/* webpackChunkName: "credit-risk" */ './pages/admin/CreditRiskFlags'));
const CreditOverLimit = lazy(() => import(/* webpackChunkName: "credit-hold" */ './pages/admin/CreditOverLimit'));
const EntityCategoriesAdmin = lazy(() => import(/* webpackChunkName: "entity-categories-admin" */ './pages/admin/EntityCategoriesAdmin'));
const CampaignBuilder = lazy(() => import(/* webpackChunkName: "campaigns" */ './pages/admin/CampaignBuilder'));
const SystemHealth = lazy(() => import(/* webpackChunkName: "system-health" */ './pages/admin/SystemHealth'));
const ReportsExports = lazy(() => import(/* webpackChunkName: "reports-exports" */ './pages/finance/ReportsExports'));
const PaymentsRegister = lazy(() => import(/* webpackChunkName: "payments-register" */ './pages/finance/PaymentsRegister'));
const ApReconciliation = lazy(() => import(/* webpackChunkName: "ap-reconciliation" */ './pages/accounting/ApReconciliation'));
const LoadProfitability = lazy(() => import(/* webpackChunkName: "load-pnl" */ './pages/LoadProfitability'));
const QuickBooksExport = lazy(() => import(/* webpackChunkName: "qb-export" */ './pages/accounting/QuickBooksExport'));
const AgingDashboard = lazy(() => import(/* webpackChunkName: "aging-dashboard" */ './pages/AgingDashboard'));
const AgedWIPReport = lazy(() => import(/* webpackChunkName: "aged-wip" */ './pages/accounting/AgedWIPReport'));
const BillingEmailsDashboard = lazy(() => import(/* webpackChunkName: "billing-emails" */ './pages/accounting/BillingEmailsDashboard'));
const UserDataHealth = lazy(() => import(/* webpackChunkName: "user-data-health" */ './pages/admin/UserDataHealth'));
const EmailHealth = lazy(() => import(/* webpackChunkName: "email-health" */ './pages/EmailHealth'));
const LeadCleanup = lazy(() => import(/* webpackChunkName: "lead-cleanup" */ './pages/LeadCleanup'));
const CustomerDataHealth = lazy(() => import(/* webpackChunkName: "customer-data-health" */ './pages/admin/CustomerDataHealth'));
const LeadDataHealth = lazy(() => import(/* webpackChunkName: "lead-data-health" */ './pages/admin/LeadDataHealth'));
// PendingLoadsByRep removed Feb 15, 2026 — route redirects to /accounting/aging
const Report1099NEC = lazy(() => import(/* webpackChunkName: "form-1099" */ './pages/accounting/Report1099NEC'));
const ProfitAggregations = lazy(() => import(/* webpackChunkName: "profit-aggregations" */ './pages/accounting/ProfitAggregations'));
const InsightsHub = lazy(() => import(/* webpackChunkName: "insights-hub" */ './pages/accounting/InsightsHub'));
const Accounting = lazy(() => import(/* webpackChunkName: "accounting" */ './pages/Accounting'));
const Approvals = lazy(() => import(/* webpackChunkName: "approvals" */ './pages/Approvals'));
const Commissions = lazy(() => import(/* webpackChunkName: "commissions" */ './pages/Commissions'));
const Opportunities = lazy(() => import(/* webpackChunkName: "opportunities" */ './pages/Opportunities'));
const CreditRequests = lazy(() => import(/* webpackChunkName: "credit-requests" */ './pages/CreditRequests'));
const HelpCenter = lazy(() => import(/* webpackChunkName: "help-center" */ './pages/help/HelpCenter'));
const HelpArticle = lazy(() => import(/* webpackChunkName: "help-center" */ './pages/help/HelpArticle'));
const CreditApplication = lazy(() => import(/* webpackChunkName: "credit-application" */ './pages/CreditApplication'));
const DocumentTemplates = lazy(() => import(/* webpackChunkName: "doc-templates" */ './pages/DocumentTemplates'));
const Leads = lazy(() => import(/* webpackChunkName: "leads" */ './pages/Leads'));
const EmailTemplates = lazy(() => import(/* webpackChunkName: "email-templates" */ './pages/EmailTemplates'));
const TasksDashboard = lazy(() => import(/* webpackChunkName: "tasks" */ './pages/TasksDashboard'));
const TeamTaskBoard = lazy(() => import(/* webpackChunkName: "tasks" */ './pages/TeamTaskBoard'));
const Sequences = lazy(() => import(/* webpackChunkName: "sequences" */ './pages/Sequences'));
const SequenceDetail = lazy(() => import(/* webpackChunkName: "sequences" */ './pages/SequenceDetail'));
const Leaderboard = lazy(() => import(/* webpackChunkName: "leaderboard" */ './pages/Leaderboard'));
const EmailAnalytics = lazy(() => import(/* webpackChunkName: "email-analytics" */ './pages/EmailAnalytics'));
const Offices = lazy(() => import(/* webpackChunkName: "offices" */ './pages/Offices'));
const FactoringCompanies = lazy(() => import(/* webpackChunkName: "factoring-companies" */ './pages/FactoringCompanies'));
const AIHub = lazy(() => import(/* webpackChunkName: "ai-hub" */ './pages/AIHub'));
const WorkflowDashboard = lazy(() => import(/* webpackChunkName: "workflow" */ './pages/WorkflowDashboard'));
const SalesUserGuide = lazy(() => import(/* webpackChunkName: "sales-guide" */ './pages/SalesUserGuide'));
const ActivityReports = lazy(() => import(/* webpackChunkName: "activity-reports" */ './pages/ActivityReports'));
const DataImport = lazy(() => import(/* webpackChunkName: "data-import" */ './pages/DataImport'));
const DuplicateLoads = lazy(() => import(/* webpackChunkName: "dup-loads" */ './pages/DuplicateLoads'));

// Customer Portal — split into its own chunk family
const CustomerPortalLogin = lazy(() => import(/* webpackChunkName: "portal-login" */ './pages/CustomerPortalLogin'));
const CustomerPortalSetPassword = lazy(() => import(/* webpackChunkName: "portal-set-password" */ './pages/CustomerPortalSetPassword'));
const CustomerPortalDashboard = lazy(() => import(/* webpackChunkName: "portal-dash" */ './pages/CustomerPortalDashboard'));
const CustomerPortalLoads = lazy(() => import(/* webpackChunkName: "portal-loads" */ './pages/CustomerPortalLoads'));
const CustomerPortalInvoices = lazy(() => import(/* webpackChunkName: "portal-invoices" */ './pages/CustomerPortalInvoices'));
const CustomerPortalDocuments = lazy(() => import(/* webpackChunkName: "portal-docs" */ './pages/CustomerPortalDocuments'));

// Build the path → factory map used for prefetching the user's last route
// after authentication. Webpack lazy factories are inert until called, so
// keeping a reference here is essentially free.
const lazyImportMap = {
  '/dashboard': () => import(/* webpackChunkName: "dashboard-prefetch" */ './pages/Dashboard'),
  '/loads': () => import(/* webpackChunkName: "loads" */ './pages/Loads'),
  '/leads': () => import(/* webpackChunkName: "leads" */ './pages/Leads'),
  '/leads': () => import(/* webpackChunkName: "leads" */ './pages/Leads'),
  '/customers': () => import(/* webpackChunkName: "customers" */ './pages/Customers'),
  '/carriers': () => import(/* webpackChunkName: "carriers" */ './pages/Carriers'),
  '/invoices': () => import(/* webpackChunkName: "invoices" */ './pages/Invoices'),
  '/accounting': () => import(/* webpackChunkName: "accounting" */ './pages/Accounting'),
  '/reports': () => import(/* webpackChunkName: "reports" */ './pages/Reports'),
  '/leaderboard': () => import(/* webpackChunkName: "leaderboard" */ './pages/Leaderboard'),
  '/opportunities': () => import(/* webpackChunkName: "opportunities" */ './pages/Opportunities'),
  '/email-templates': () => import(/* webpackChunkName: "email-templates" */ './pages/EmailTemplates'),
  '/tasks': () => import(/* webpackChunkName: "tasks" */ './pages/TasksDashboard'),
  '/team-tasks': () => import(/* webpackChunkName: "tasks" */ './pages/TeamTaskBoard'),
  '/sequences': () => import(/* webpackChunkName: "sequences" */ './pages/Sequences'),
  '/workflow': () => import(/* webpackChunkName: "workflow" */ './pages/WorkflowDashboard'),
  '/users': () => import(/* webpackChunkName: "users" */ './pages/Users'),
  '/offices': () => import(/* webpackChunkName: "offices" */ './pages/Offices'),
  '/settings': () => import(/* webpackChunkName: "settings" */ './pages/Settings'),
  '/settings/margin-goals': () => import(/* webpackChunkName: "margin-goals" */ './pages/MarginGoalSetting'),
  '/integrations': () => import(/* webpackChunkName: "integrations" */ './pages/Integrations'),
  '/approvals': () => import(/* webpackChunkName: "approvals" */ './pages/Approvals'),
  '/commissions': () => import(/* webpackChunkName: "commissions" */ './pages/Commissions'),
  '/credit-requests': () => import(/* webpackChunkName: "credit-requests" */ './pages/CreditRequests'),
  '/ai-hub': () => import(/* webpackChunkName: "ai-hub" */ './pages/AIHub'),
  '/email-analytics': () => import(/* webpackChunkName: "email-analytics" */ './pages/EmailAnalytics'),
  '/activity-reports': () => import(/* webpackChunkName: "activity-reports" */ './pages/ActivityReports'),
  '/factoring-companies': () => import(/* webpackChunkName: "factoring-companies" */ './pages/FactoringCompanies'),
  '/document-templates': () => import(/* webpackChunkName: "doc-templates" */ './pages/DocumentTemplates'),
  '/scheduled-reports': () => import(/* webpackChunkName: "scheduled-reports" */ './pages/ScheduledReports'),
  '/data-import': () => import(/* webpackChunkName: "data-import" */ './pages/DataImport'),
  '/duplicate-loads': () => import(/* webpackChunkName: "dup-loads" */ './pages/DuplicateLoads'),
};
registerRouteFactories(lazyImportMap);

// Suspense fallback used while a route chunk is fetching. Keep it lightweight
// — anything heavier defeats the point of code-splitting.
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen" data-testid="page-loader">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? children : <Navigate to="/login" />;
};

const P = ({ children }) => (
  <ProtectedRoute><Layout>{children}</Layout></ProtectedRoute>
);

// Root "/" — redirect non-admins to their role-default landing page so they
// don't have to click through the sidebar on every login. Admins (and users
// who explicitly set `default_landing_page` to `/dashboard`) still get the
// full executive Dashboard.
const RootRedirector = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const target = getDefaultLandingPage(user);
  if (target === '/dashboard' || target === '/') {
    return <ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>;
  }
  return <Navigate to={target} replace />;
};

function RouteVisitTracker() {
  const location = useLocation();
  const { user, API } = useAuth();
  const lastPinged = React.useRef({});
  useEffect(() => { recordVisit(location.pathname); }, [location.pathname]);
  // Server-side screen usage: one fire-and-forget ping per screen change, used
  // by the monthly "nobody opened these screens" digest. Never blocks the UI.
  // Guarded so a remount (or StrictMode's double effect) can't inflate counts.
  useEffect(() => {
    if (!user || !API) return;
    const now = Date.now();
    const key = `${user.id}:${location.pathname}`;
    if (now - (lastPinged.current[key] || 0) < 60000) return;
    lastPinged.current[key] = now;
    axios.post(`${API}/usage/screen`, { path: location.pathname }).catch(() => {});
  }, [location.pathname, user, API]);
  return null;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <OfficeSwitcherProvider>
        <RouteVisitTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes (eager) */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/credit-application/:token" element={<CreditApplication />} />
          <Route path="/track/:token" element={<Suspense fallback={null}><DriverTrackingPage /></Suspense>} />
          <Route path="/track/share/:shareToken" element={<Suspense fallback={null}><PublicTrackingPage /></Suspense>} />
          <Route path="/lane/:reference" element={<Suspense fallback={null}><PublicLanePage /></Suspense>} />
          <Route path="/eld/oauth/success" element={<Suspense fallback={null}><EldOAuthSuccess /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={null}><PrivacyPolicy /></Suspense>} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />

          {/* Carrier Portal — public auth pages */}
          <Route path="/carrier-portal/login" element={<Suspense fallback={null}><CarrierPortalLogin /></Suspense>} />
          <Route path="/carrier-portal/signup" element={<Suspense fallback={null}><CarrierPortalSignup /></Suspense>} />
          <Route path="/carrier-portal/forgot-password" element={<Suspense fallback={null}><CarrierPortalForgotPassword /></Suspense>} />
          <Route path="/carrier-portal/set-password/:token" element={<Suspense fallback={null}><CarrierPortalSetPassword /></Suspense>} />

          {/* Carrier Portal — authenticated layout + nested routes */}
          <Route path="/carrier-portal" element={<Suspense fallback={null}><CarrierPortalLayout /></Suspense>}>
            <Route index element={<Suspense fallback={null}><CarrierPortalDashboard /></Suspense>} />
            <Route path="loads" element={<Suspense fallback={null}><CarrierPortalLoads /></Suspense>} />
            <Route path="loads/:loadId" element={<Suspense fallback={null}><CarrierPortalLoadDetail /></Suspense>} />
            <Route path="payments" element={<Suspense fallback={null}><CarrierPortalPayments /></Suspense>} />
            <Route path="eld" element={<Suspense fallback={null}><CarrierPortalEld /></Suspense>} />
            <Route path="users" element={<Suspense fallback={null}><CarrierPortalUsers /></Suspense>} />
          </Route>

          {/* Redirects */}
          <Route path="/email-parser" element={<Navigate to="/opportunities" replace />} />
          <Route path="/documents" element={<Navigate to="/loads" replace />} />
          <Route path="/invoices/followup-effectiveness" element={<Navigate to="/invoices" replace />} />
          {/* Quoting was removed in June 2026 — old links land on the lane desk instead. */}
          <Route path="/quotes" element={<Navigate to="/opportunities" replace />} />
          <Route path="/sales-analytics" element={<Navigate to="/reports" replace />} />
          <Route path="/agents" element={<Navigate to="/users" replace />} />
          <Route path="/analytics" element={<Navigate to="/reports" replace />} />
          <Route path="/eod-report" element={<Navigate to="/reports" replace />} />
          <Route path="/office-dashboard" element={<Navigate to="/reports" replace />} />

          {/* Core Routes */}
          <Route path="/" element={<RootRedirector />} />
          <Route path="/dashboard" element={<P><Dashboard /></P>} />
          <Route path="/office-pulse" element={<Navigate to="/accounting/insights" replace />} />
          <Route path="/loads" element={<P><Loads /></P>} />
          <Route path="/loads/:loadId" element={<P><LoadDetail /></P>} />
          <Route path="/loads/:loadId/edit" element={<P><LoadEdit /></P>} />
          <Route path="/loads/:loadId/bol" element={<P><BolEdit /></P>} />
          <Route path="/loads/create" element={<P><LoadEdit /></P>} />
          <Route path="/customers" element={<P><Customers /></P>} />
          <Route path="/customers/:customerId" element={<P><CustomerRecordPage /></P>} />
          <Route path="/carriers" element={<P><Carriers /></P>} />
          <Route path="/carriers/duplicates" element={<P><CarrierDuplicates /></P>} />
          <Route path="/admin/customer-duplicates" element={<P><CustomerDuplicates /></P>} />
          <Route path="/my-day" element={<P><MyDay /></P>} />
          <Route path="/leads" element={<P><Leads /></P>} />
          <Route path="/leads/:leadId" element={<P><LeadRecordPage /></P>} />
          <Route path="/opportunities" element={<P><Opportunities /></P>} />

          {/* Financial */}
          <Route path="/invoices" element={<P><Invoices /></P>} />
          <Route path="/admin/data-health" element={<P><DataHealthConsole /></P>} />
          <Route path="/admin/office-isolation" element={<P><OfficeIsolation /></P>} />
          <Route path="/accounting" element={<P><Accounting /></P>} />
          <Route path="/approvals" element={<P><Approvals /></P>} />
          <Route path="/commissions" element={<P><Commissions /></P>} />
          <Route path="/credit-requests" element={<P><CreditRequests /></P>} />
          <Route path="/help" element={<P><HelpCenter /></P>} />
          <Route path="/help/:slug" element={<P><HelpArticle /></P>} />

          {/* Reports & Analytics */}
          <Route path="/reports" element={<P><Reports /></P>} />
          <Route path="/scheduled-reports" element={<P><ScheduledReports /></P>} />
          <Route path="/email-analytics" element={<P><EmailAnalytics /></P>} />
          <Route path="/leaderboard" element={<P><Leaderboard /></P>} />

          {/* Admin & Config */}
          <Route path="/users" element={<P><Users /></P>} />
          <Route path="/offices" element={<P><Offices /></P>} />
          <Route path="/factoring-companies" element={<P><FactoringCompanies /></P>} />
          <Route path="/integrations" element={<P><Integrations /></P>} />
          <Route path="/document-templates" element={<P><DocumentTemplates /></P>} />
          <Route path="/data-import" element={<P><DataImport /></P>} />
          <Route path="/duplicate-loads" element={<P><DuplicateLoads /></P>} />
          <Route path="/settings" element={<P><Settings /></P>} />
          <Route path="/admin/mailbox-health" element={<P><MailboxHealth /></P>} />
          <Route path="/admin/email-pipeline-health" element={<P><EmailPipelineHealthPage /></P>} />
          <Route path="/admin/ownership-transfers" element={<P><OwnershipTransfers /></P>} />
          <Route path="/admin/security-log" element={<P><SecurityLogPage /></P>} />
          <Route path="/settings/margin-goals" element={<P><MarginGoalSetting /></P>} />
          <Route path="/notifications" element={<P><Notifications /></P>} />
          <Route path="/admin/orphaned-documents" element={<P><OrphanedDocuments /></P>} />
          <Route path="/admin/missing-dates" element={<P><MissingDatesAdmin /></P>} />
          <Route path="/admin/orphan-invoices" element={<P><OrphanInvoicesPage /></P>} />
          <Route path="/admin/missing-carrier" element={<P><MissingCarrierAdmin /></P>} />
          <Route path="/admin/loads-audit" element={<P><LoadsAudit /></P>} />
          <Route path="/admin/disputes" element={<P><OpenDisputes /></P>} />
          <Route path="/admin/grace-periods" element={<P><GracePeriodsAudit /></P>} />
          <Route path="/admin/invoice-reminders" element={<P><InvoiceRemindersSettingsPage /></P>} />
          <Route path="/admin/notifications-center" element={<P><NotificationsCenterPage /></P>} />
          <Route path="/admin/duplicate-invoices" element={<P><DuplicateInvoicesPage /></P>} />
          <Route path="/admin/imap-sent-mirror" element={<P><ImapSentMirrorPage /></P>} />
          <Route path="/admin/sales-rep-health" element={<P><SalesRepHealth /></P>} />
          <Route path="/admin/mailgun-suppressions" element={<P><MailgunSuppressions /></P>} />
          <Route path="/admin/email-hub" element={<P><EmailManagementHub /></P>} />
          <Route path="/admin/credit-risk" element={<P><CreditRiskFlags /></P>} />
          <Route path="/admin/credit-hold" element={<P><CreditOverLimit /></P>} />
          <Route path="/admin/entity-categories" element={<P><EntityCategoriesAdmin /></P>} />
          <Route path="/admin/campaigns" element={<P><CampaignBuilder /></P>} />
          <Route path="/admin/system-health" element={<P><SystemHealth /></P>} />
          <Route path="/finance/reports-exports" element={<P><ReportsExports /></P>} />
          <Route path="/finance/payments" element={<P><PaymentsRegister /></P>} />
          <Route path="/accounting/ap-reconciliation" element={<P><ApReconciliation /></P>} />
          <Route path="/accounting/profitability" element={<Navigate to="/loads?tab=profitability" replace />} />
          <Route path="/accounting/carrier-settlements" element={<Navigate to="/accounting" replace />} />
          <Route path="/accounting/quickbooks-export" element={<P><QuickBooksExport /></P>} />
          {/* Collections tab was removed Feb 2026 — redirect legacy links to Aging. */}
          <Route path="/accounting/collections" element={<Navigate to="/accounting/aging" replace />} />
          <Route path="/accounting/aging" element={<P><AgingDashboard /></P>} />
          <Route path="/accounting/kpi-dashboard" element={<Navigate to="/accounting/aging" replace />} />
          <Route path="/accounting/profit-aggregations" element={<P><ProfitAggregations /></P>} />
          <Route path="/accounting/insights" element={<P><InsightsHub /></P>} />
          <Route path="/accounting/aged-wip" element={<P><AgedWIPReport /></P>} />
          <Route path="/accounting/billing-emails" element={<P><BillingEmailsDashboard /></P>} />
          <Route path="/admin/user-data-health" element={<P><UserDataHealth /></P>} />
          <Route path="/admin/email-health" element={<P><EmailHealth /></P>} />
          <Route path="/admin/lead-cleanup" element={<P><LeadCleanup /></P>} />
          <Route path="/admin/customer-data-health" element={<P><CustomerDataHealth /></P>} />
          <Route path="/admin/lead-data-health" element={<P><LeadDataHealth /></P>} />
          <Route path="/accounting/pending-loads-by-rep" element={<Navigate to="/accounting/aging" replace />} />
          <Route path="/accounting/1099-nec" element={<P><Report1099NEC /></P>} />

          {/* Tools */}
          <Route path="/operations-center" element={<Navigate to="/workflow" replace />} />
          <Route path="/email-templates" element={<P><EmailTemplates /></P>} />
          <Route path="/tasks" element={<P><TasksDashboard /></P>} />
          <Route path="/team-tasks" element={<P><TeamTaskBoard /></P>} />
          <Route path="/sequences" element={<P><Sequences /></P>} />
          <Route path="/sequences/:sequenceId" element={<P><SequenceDetail /></P>} />
          <Route path="/ai-hub" element={<P><AIHub /></P>} />
          <Route path="/workflow" element={<P><WorkflowDashboard /></P>} />
          <Route path="/activity-reports" element={<P><ActivityReports /></P>} />
          <Route path="/sales-user-guide" element={<P><SalesUserGuide /></P>} />

          {/* Customer Portal Routes (no Layout) */}
          <Route path="/clientportal/login" element={<CustomerPortalLogin />} />
          <Route path="/clientportal/set-password/:token" element={<CustomerPortalSetPassword />} />
          <Route path="/clientportal" element={<CustomerPortalDashboard />} />
          <Route path="/clientportal/dashboard" element={<CustomerPortalDashboard />} />
          <Route path="/clientportal/loads" element={<CustomerPortalLoads />} />
          <Route path="/clientportal/invoices" element={<CustomerPortalInvoices />} />
          <Route path="/clientportal/documents" element={<CustomerPortalDocuments />} />
          </Routes>
        </Suspense>
      </OfficeSwitcherProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <CompanyProvider>
        <AuthProvider>
          <ImpersonationBanner />
          <ForcePasswordChangeModal />
          <EntityCategoriesProvider>
            <AppRoutes />
          </EntityCategoriesProvider>
          <Toaster position="top-right" />
        </AuthProvider>
      </CompanyProvider>
    </ErrorBoundary>
  );
}

export default App;
